import { getDownloadURL, getMetadata, getStorage, ref } from "firebase/storage";
import { removePopUp } from "../../../components/deprecated/Popup/deprecated_PopUps";
import { FirebaseEvents } from "../../../utils/firebaseEvents";
import { t } from "i18next";
import { Utilities } from "../../../utils/utilities";
import { FirebaseData } from "./FirebaseData";
import { downloadExternalFileURL, downloadFirebaseFile, downloadFunctionsFileURL } from "./file.utils";
import { ReadAccess } from "./AccessData";
import { DataCreator } from "../../../utils/dataCreator";
import { Tag } from "../../../data-hooks/company/tags/tags.type";
import { LocalData } from "./LocalData";
import { ToastUtilities } from "@libs/react-toastify/ToastUtilities";

export class FileDocumentData extends FirebaseData {
  static SUPPORTED_DATA_TYPES = [
    "pdf",
    "docx",
    "doc",
    "dotx",
    "ppt",
    "pptx",
    "xlsx",
    "xls",
    "xlsm",
    "xlsb",
    "csv",
    "msg",
    "dwg",
    "obj",
    "eml",
    "db",
    "zip",
    "axd",
    "ifc",
    "rvt",
    "smi",
    "htm",
    "html",
    "thmx",
    "xml",
    "pub",
    "bak",
    "log",
    "fepx",
  ];

  static SUPPORTED_IMAGE_TYPES = ["jpg", "jpeg", "png", "gif", "bmp", "svg", "tiff", "heic"];

  static SUPPORTED_VIDEO_TYPES = ["mp4", "mov"];

  static SUPPORTED_AUDIO_TYPES = ["wav", "mp3"];

  static ALL_SUPPORTED_TYPES = [
    ...FileDocumentData.SUPPORTED_DATA_TYPES,
    ...FileDocumentData.SUPPORTED_IMAGE_TYPES,
    ...FileDocumentData.SUPPORTED_VIDEO_TYPES,
    ...FileDocumentData.SUPPORTED_AUDIO_TYPES,
  ];

  name = "";
  type?: string;
  path?: string;
  previewPath?: string;
  externalURL?: string;
  size?: number;
  externalFileClientID = "";
  fileURL?: string;
  fileLoading = false;
  fileLoaded = false;
  fileLoadedListeners: (() => void)[] = [];
  tags: Tag[] = [];
  readAccess: ReadAccess = {};
  archived = false;

  setup(data: any) {
    this.path = typeof data.path === "string" ? data.path : undefined;
    this.previewPath = typeof data.previewPath === "string" ? data.previewPath : undefined;
    this.name = typeof data.name === "string" ? data.name : "";
    this.type = typeof data.type === "string" ? data.type : undefined;
    this.size = typeof data.size === "number" ? data.size : undefined;
    this.externalURL = typeof data.externalURL === "string" ? data.externalURL : undefined;
    this.externalFileClientID = typeof data.externalFileClientID === "string" ? data.externalFileClientID : "";

    this.tags = [];
    if (Array.isArray(data.tags?.standard)) {
      for (const tag of data.tags?.standard) {
        const tagData = LocalData.getTagWithId(tag);
        if (tagData) {
          this.tags.push(tagData);
        }
      }
    }

    this.readAccess = Utilities.getReadAccess(data.readAccess);

    this.archived = typeof data.archived === "boolean" ? data.archived : false;

    super.setup(data);
  }

  rename(name: string) {
    return this.saveUpdate({
      name,
    });
  }

  loadFileURL(callback: () => void): void {
    const storage = getStorage();
    this.fileLoadedListeners.push(callback);
    if (!this.fileLoading && this.path) {
      const reference = ref(storage, this.path);
      this.fileLoading = true;
      getDownloadURL(reference)
        .then((url) => {
          if (typeof url === "string") {
            this.fileURL = url;
          } else {
            this.deleted = true;
          }
        })
        .catch(() => {
          this.deleted = true;
        })
        .finally(() => {
          this.fileLoading = false;
          this.fileLoaded = true;
          for (const listener of this.fileLoadedListeners) {
            listener();
          }
        });
    }
  }

  loadStorageMetadata(callback: () => void) {
    const storage = getStorage();
    if (this.path) {
      const reference = ref(storage, this.path);
      getMetadata(reference)
        .then((metadata) => {
          if (!this.size && typeof metadata.size === "number") this.size = metadata.size;
          if (!this.createdBy?.date && typeof metadata.timeCreated === "string") {
            if (!this.createdBy) this.createdBy = { date: new Date(metadata.timeCreated) };
            else if (this.createdBy) this.createdBy.date = new Date(metadata.timeCreated);
          }
        })
        .finally(() => {
          callback();
        });
    }
  }

  deleteFile() {
    return this.saveUpdate({
      deletedBy: DataCreator.getDeletedBy(),
    });
  }

  downloadFile() {
    if (this.fileURL) {
      FirebaseEvents.logEvent("tenant_file_opened", {
        file_type: this.type ?? "",
      });
      return downloadFirebaseFile(this.fileURL, this.name);
    } else if (this.externalURL) {
      return downloadExternalFileURL(this.externalURL, this.name);
    } else if (this.externalFileClientID) {
      return downloadFunctionsFileURL(this.parent?.id ?? "", this.id, this.name);
    } else {
      ToastUtilities.presentInfo(t("main.loading"));
      this.loadFileURL(() => {
        removePopUp();
        if (this.fileURL) {
          downloadFirebaseFile(this.fileURL, this.name);
          FirebaseEvents.logEvent("tenant_file_opened", {
            file_type: this.type ?? "",
          });
        }

        if (this.externalURL) {
          return downloadExternalFileURL(this.externalURL, this.name);
        }
      });
    }
  }

  archive() {
    const newArchiveStatus = !this.archived;
    return this.saveUpdate({ archived: newArchiveStatus }).then(() => {
      this.archived = newArchiveStatus;
    });
  }
}
